import React from 'react';
import { Crop } from 'react-image-crop';
import { Box, Button, Typography, styled } from '@mui/material';
import { v4 as uuid } from 'uuid';
import { contentApi } from 'components/content/contentApi';
import { resizeImage } from 'lib/helpers';
import logger from 'lib/logger';
import { isString } from 'lib/typeguards';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Column, Row } from 'ui/Flex';
import { MuiModal } from 'ui/MuiModal';
import { MuiDropZone } from './MuiDropZone';
import { MuiReactImageCrop } from './MuiReactImageCrop';

const MAX_FILE_SIZE = 10000000;

const PREFIX = 'MuiImagePicker';

const classes = {
    paper: `${PREFIX}-paper`,
    column: `${PREFIX}-column`
};

const StyledDiv = styled('div')({
    width: '100%',
    [`& .${classes.paper}`]: {
        padding: 0,
        overflow: 'hidden'
    }
});

const StyledRow = styled(Row)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    [`& .${classes.column}`]: {
        minHeight: theme.spacing(5)
    }
}));

interface BlobWithName extends Blob {
    name?: string;
}

export interface IAdditionalUpload {
    width: number;
    height: number;
    uploadFolder: string;
    fileName: string;
    format: string;
}

interface MuiImagePickerProps {
    onSelect: (imageUrl: string) => void;
    height?: number;
    url?: string;
    // Due to upgrading material ui we had to do that, please remove legacy forms finally
    width?: number | any;
    aspect?: number;
    unremovable?: boolean;
    fullWidth?: boolean;
    uploadFileName?: string;
    uploadFolder?: string;
    overlay?: React.ReactNode;
    disableCrop?: boolean;
    additionalUploads?: IAdditionalUpload[];
    smallText?: boolean;
    accept?: string | string[];
}

interface IState {
    src: string;
    imagePickerStep: MuiImagePickerStep;
    crop: Crop;
    error: null;
    resultImageUrl: string;
    isLoading?: boolean;
    disabled?: boolean;
    blob?: BlobWithName;
}

enum MuiImagePickerStep {
    CLOSED = 'CLOSED',
    FILE_CHOOSING = 'FILE_CHOOSING',
    CROPING = 'CROPING'
}

const getDefaultCrop = (props: MuiImagePickerProps) => {
    let aspect: number;
    if (props.aspect) {
        // eslint-disable-next-line prefer-destructuring
        aspect = props.aspect;
    } else {
        aspect = props.width && props.height ? props.width / props.height : null;
    }
    return {
        aspect,
        width: props.width,
        height: props.height,
        x: 0,
        y: 0
    };
};

export class MuiImagePicker extends React.PureComponent<MuiImagePickerProps, IState> {
    imageRef: HTMLImageElement;
    fileUrl: string;
    constructor(props: MuiImagePickerProps) {
        super(props);
        this.state = {
            src: null,
            disabled: false,
            error: null,
            isLoading: false,
            imagePickerStep: MuiImagePickerStep.CLOSED,
            resultImageUrl: props.url ? `${props.url}?t=${new Date().toTimeString()}` : props.url,
            crop: getDefaultCrop(props)
        };
    }

    onSelectFile = (src: string | ArrayBuffer) => {
        if (isString(src)) {
            this.setState({
                src,
                imagePickerStep: MuiImagePickerStep.CROPING,
                isLoading: true,
                disabled: true
            });
        }
    };

    onImageLoaded = (image: HTMLImageElement) => {
        this.imageRef = image;
        this.makeClientCrop({ ...this.state.crop, height: image.height, width: image.width });
    };

    onCropComplete = (crop: Crop) => {
        this.makeClientCrop(crop);
    };

    onCropChange = (crop: Crop) => {
        this.setState({ crop });
    };

    async makeClientCrop(crop: Crop) {
        if (this.imageRef && crop.width && crop.height) {
            this.setState({ isLoading: true, disabled: true, error: null });
            try {
                const { blob } = await this.getCroppedImg(
                    this.imageRef,
                    crop,
                    this.props.uploadFileName ?? `${uuid()}` // For compatibility
                );
                this.setState({ blob, isLoading: false, disabled: false, error: null });
            } catch (error) {
                this.setState({ error: error.message, isLoading: false });
            }
        }
    }

    getCroppedImg(image: HTMLImageElement, crop: Crop, fileName: string): Promise<{ blob: Blob }> {
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width * scaleX;
        canvas.height = crop.height * scaleY;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width * scaleX,
            crop.height * scaleY
        );

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob: BlobWithName) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                }
                if (blob.size >= MAX_FILE_SIZE) {
                    reject(new Error('Maximum file size exceeded. File should be under 10M.'));
                }
                blob.name = fileName;
                resolve({ blob });
            });
        });
    }

    handleClose = () => {
        this.setState({
            imagePickerStep: MuiImagePickerStep.CLOSED,
            src: null,
            crop: getDefaultCrop(this.props)
        });
    };

    handleSubmit = async () => {
        this.setState({ isLoading: true, error: null });
        const formData = new FormData();
        formData.append('files', this.state.blob, this.state.blob.name);
        try {
            const result = await contentApi.media.uploadFile(
                { body: formData, skipStringify: true },
                this.props.uploadFolder
            );
            if (!result.ok) {
                throw new Error('Failed to upload an image');
            }

            // There may be additional uploads to consider, with specific sizes
            // This is used in branding images, for iOS icons and such.
            if (this.props.additionalUploads) {
                await Promise.all(
                    this.props.additionalUploads.map(
                        async ({ width, height, format, uploadFolder, fileName }) => {
                            const resizedFile = await resizeImage(this.state.blob, width, height, format);
                            const formData = new FormData();
                            formData.append('files', resizedFile, fileName);
                            await contentApi.media.uploadFile(
                                { body: formData, skipStringify: true },
                                uploadFolder
                            );
                        }
                    )
                );
            }
            // TODO: find a way to do it better
            const resultImageUrl = result.body.items[0].url;
            this.props.onSelect(resultImageUrl);
            const date = new Date().toTimeString();
            this.setState({ resultImageUrl: `${resultImageUrl}?${date}`, isLoading: false });
            this.handleClose();
        } catch (e) {
            logger.error('Failed to upload an image', e);
            this.setState({ error: e.message, isLoading: false });
        }
    };

    clearImage = () => {
        this.props.onSelect(null);
        this.setState({ resultImageUrl: null });
    };
    render() {
        const { crop, src, imagePickerStep, disabled, resultImageUrl, error, isLoading } = this.state;
        const { unremovable, fullWidth, overlay, disableCrop, smallText, accept } = this.props;

        return (
            <StyledDiv>
                <MuiDropZone
                    aspect={crop.aspect}
                    onSelect={this.onSelectFile}
                    imageUrl={resultImageUrl}
                    fullWidth={fullWidth}
                    overlay={overlay}
                    disableCrop={disableCrop}
                    smallText={smallText}
                    accept={accept}
                />
                {!unremovable && (
                    <Box pt={1}>
                        <Button color="secondary" variant="outlined" size="small" onClick={this.clearImage}>
                            Clear Image
                        </Button>
                    </Box>
                )}

                {src && (
                    <MuiModal
                        open={imagePickerStep === MuiImagePickerStep.CROPING}
                        style={{ maxWidth: 800 }}
                        onClose={this.handleClose}
                        className={classes.paper}
                    >
                        <Column>
                            <MuiReactImageCrop
                                src={src}
                                crop={crop}
                                disabled={disableCrop}
                                onImageLoaded={this.onImageLoaded}
                                onComplete={this.onCropComplete}
                                onChange={this.onCropChange}
                            />
                            <StyledRow flex={1} align="space-between" valign="center">
                                <Column className={classes.column} valign="center">
                                    <Typography color="error">{error}</Typography>
                                    {!disableCrop && (
                                        <Typography variant="caption">
                                            Drag and drop to crop the image
                                        </Typography>
                                    )}
                                </Column>
                                <Row align="flex-end" gutter>
                                    <Button color="secondary" onClick={this.handleClose} variant="outlined">
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        color="primary"
                                        variant="contained"
                                        onClick={this.handleSubmit}
                                        loading={isLoading}
                                        disabled={disabled}
                                    >
                                        Submit
                                    </LoadingButton>
                                </Row>
                            </StyledRow>
                        </Column>
                    </MuiModal>
                )}
            </StyledDiv>
        );
    }
}
